// /parts/lookup — one field that takes any Firefly sticker and opens its page.

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { useHistory } from 'react-router-dom';
import SearchField from '../components/ui/SearchField';
import { scanDestination } from '../utils/scanDestination';
import { LOOKUP_MISS_TEXT, useStickerLookup } from '../utils/useStickerLookup';
import PageLayout from '../components/ui/PageLayout';
import FadeIn from '../components/ui/FadeIn';

const BarcodeScannerModal = lazy(() => import('../components/BarcodeScanner/BarcodeScannerModal'));

const PartLookupPage: React.FC = () => {
  const history = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);
  const { lookup, cancel, markMiss, resolving, miss, missCount } = useStickerLookup();

  const [value, setValue] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Selected, so the next wedge scan replaces the missed code instead of
  // appending to it.
  const refocus = () => {
    inputRef.current?.focus();
    inputRef.current?.select();
  };

  const resolve = async (raw: string) => {
    const outcome = await lookup(raw);
    if (outcome.status === 'miss') {
      refocus();
      return;
    }
    if (outcome.status !== 'found') return;
    const to = scanDestination(outcome.result);
    if (to) {
      history.push(to);
    } else {
      markMiss('not_found');
      refocus();
    }
  };

  const onCameraScan = ({ text }: { text: string }) => {
    setCameraOpen(false);
    setValue(text);
    resolve(text);
  };

  return (
    <PageLayout
      kind="focus"
      className="pt-10 sm:pt-16"
      header={<h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-on-surface">Scan Part</h1>}
    >
      <p id="part-lookup-help" className="mt-1 text-sm text-on-surface-variant">
        Scan any sticker: a part, a board, a drone, or an asset.
      </p>

      <form
        className="mt-6"
        onSubmit={(e) => {
          e.preventDefault();
          resolve(value);
        }}
      >
        <SearchField
          ref={inputRef}
          id="part-lookup-code"
          size="lg"
          mono
          label="Sticker code"
          value={value}
          onChange={(next) => {
            cancel();
            setValue(next);
          }}
          placeholder="Scan or type a code"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          aria-describedby={miss ? 'part-lookup-miss' : 'part-lookup-help'}
          aria-invalid={miss ? true : undefined}
          busy={resolving}
          className={miss ? 'rounded-full ring-2 ring-red-500/60' : ''}
          trailing={
            <Tooltip label="Open the camera and point it at the sticker">
              <IconButton label="Scan with the camera" size="lg" onClick={() => setCameraOpen(true)}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M9.2 4a1.5 1.5 0 0 0-1.3.8L7.2 6H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3h-2.2l-.7-1.2a1.5 1.5 0 0 0-1.3-.8H9.2ZM12 17.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
                  />
                </svg>
              </IconButton>
            </Tooltip>
          }
        />
      </form>

      <div className="mt-3 min-h-[1.5rem] px-5 text-sm" aria-live="polite">
        {resolving && <FadeIn><p className="text-on-surface-variant">Looking that up...</p></FadeIn>}
        {!resolving && miss && (
          <p
            key={missCount}
            id="part-lookup-miss"
            className="text-red-300 motion-safe:animate-rowLand"
          >
            {LOOKUP_MISS_TEXT[miss]}
          </p>
        )}
      </div>

      {cameraOpen && (
        <Suspense fallback={null}>
          <BarcodeScannerModal
            open={cameraOpen}
            onClose={() => {
              setCameraOpen(false);
              refocus();
            }}
            onScan={onCameraScan}
            formats={['qr']}
            title="Scan a sticker"
            hint="Point the camera at the sticker's QR code"
          />
        </Suspense>
      )}
    </PageLayout>
  );
};

export default PartLookupPage;
